import { useState } from 'react';
import { YStack, ScrollView } from 'tamagui';
import { useAuthContext } from '../../auth';
import './profile.css';

interface ProfileProps {
  onNavigateToLogin?: () => void;
}

export const Profile = ({ onNavigateToLogin }: ProfileProps) => {
  const { user, logout } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: user?.name || '',
  });
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  if (!user) {
    return (
      <ScrollView flex={1} backgroundColor="$background">
        <YStack className="profile-container">
          <div className="profile-not-found">
            <div className="not-found-icon">🔐</div>
            <h1 className="not-found-title">Access Denied</h1>
            <p className="not-found-subtitle">Please login to view your profile</p>
            <button 
              className="btn-primary-large"
              onClick={onNavigateToLogin}
            >
              Go to Login
            </button>
          </div>
        </YStack>
      </ScrollView>
    );
  }

  const handleSave = async () => {
    try {
      setSaveMessage(null);
      // TODO: Implement profile update via Supabase
      setSaveMessage({ type: 'success', text: 'Profile updated successfully' });
      setIsEditing(false);
    } catch (err) {
      setSaveMessage({ type: 'error', text: 'Failed to update profile' });
    }
  };

  const handleCancel = () => {
    setEditData({
      name: user.name || '',
    });
    setIsEditing(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      if (onNavigateToLogin) {
        onNavigateToLogin();
      }
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const getInitials = (email: string) => {
    return email
      .split('@')[0]
      .split('.')
      .map((part) => part.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  };

  return (
    <ScrollView flex={1} backgroundColor="$background">
      <YStack className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <h1 className="profile-title">My Profile</h1>
          <p className="profile-subtitle">Manage your account information</p>
        </div>

        {/* Alert Messages */}
        {saveMessage && (
          <div className={`profile-alert ${saveMessage.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            {saveMessage.type === 'success' ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17 8L8.5 16.5L3 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
                <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
            <span>{saveMessage.text}</span>
          </div>
        )}

        <div className="profile-grid">
          {/* Main Profile Card */}
          <div className="profile-card profile-card-main">
            <div className="profile-card-header">
              <div className="profile-avatar">
                {getInitials(user.email)}
              </div>
              <div className="profile-user-info">
                <h2 className="profile-user-name">{user.name || 'User'}</h2>
                <p className="profile-user-email">{user.email}</p>
              </div>
            </div>

            <div className="profile-divider"></div>

            <div className="profile-form">
              {/* Full Name Field */}
              <div className="profile-form-group">
                <label htmlFor="name" className="profile-form-label">Full Name</label>
                {isEditing ? (
                  <div className={`profile-input-wrapper ${focusedField === 'name' ? 'focused' : ''}`}>
                    <svg className="profile-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 10C12.2091 10 14 8.20914 14 6C14 3.79086 12.2091 2 10 2C7.79086 2 6 3.79086 6 6C6 8.20914 7.79086 10 10 10ZM3 18C3.5304 16.3957 4.60541 15.0593 6 14.2907C7.39459 13.5221 9.06935 13.2 10.5 13.2H11.5C13.9118 13.2 16.2353 14.0736 17.8995 15.6005C18.6167 16.2829 19.2624 17.0347 19.8 17.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      className="profile-input"
                    />
                  </div>
                ) : (
                  <div className="profile-field-display">
                    <span>{user.name || '(Not set)'}</span>
                  </div>
                )}
              </div>

              {/* Email Field (Read-only) */}
              <div className="profile-form-group">
                <label htmlFor="email" className="profile-form-label">Email Address</label>
                <div className="profile-field-display read-only">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2 4L9 10L16 4M2 4H16C17.1046 4 18 4.89543 18 6V14C18 15.1046 17.1046 16 16 16H4C2.89543 16 2 15.1046 2 14V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{user.email}</span>
                </div>
                <p className="profile-field-hint">Email cannot be changed</p>
              </div>

              {/* Role Field (Read-only) */}
              <div className="profile-form-group">
                <label htmlFor="role" className="profile-form-label">Role</label>
                <div className="profile-field-display read-only">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM10 4C13.3137 4 16 6.68629 16 10C16 11.1819 15.6916 12.2918 15.1277 13.2497L4.75034 2.8723C5.70825 2.30835 6.81814 2 8 2H10Z" fill="currentColor"/>
                  </svg>
                  <span>{user.role.charAt(0).toUpperCase() + user.role.slice(1)}</span>
                </div>
              </div>

              {/* Member Since Field (Read-only) */}
              <div className="profile-form-group">
                <label htmlFor="memberSince" className="profile-form-label">Member Since</label>
                <div className="profile-field-display read-only">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5.5 2H14.5C15.3284 2 16 2.67157 16 3.5V16.5C16 17.3284 15.3284 18 14.5 18H5.5C4.67157 18 4 17.3284 4 16.5V3.5C4 2.67157 4.67157 2 5.5 2ZM6 4V16H14V4H6ZM7 6H13V8H7V6ZM7 10H13V12H7V10Z" fill="currentColor"/>
                  </svg>
                  <span>{user.createdAt.toLocaleDateString()}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="profile-actions">
                {isEditing ? (
                  <>
                    <button 
                      className="btn-primary-submit"
                      onClick={handleSave}
                    >
                      Save Changes
                    </button>
                    <button 
                      className="btn-secondary-submit"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button 
                    className="btn-primary-submit"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Logout Card */}
          <div className="profile-card profile-card-logout">
            <div className="logout-icon">🚪</div>
            <h3 className="logout-title">Sign Out</h3>
            <p className="logout-subtitle">Sign out of your account and end your session</p>
            <button 
              className="btn-logout"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </YStack>
    </ScrollView>
  );
};
